import { useMemo, useState } from 'react';
import type { ChartDataset } from 'chart.js';
import { X } from 'lucide-react';
import { ChartBox } from '../components/ChartBox';
import { BlockerTrendPanel } from '../components/BlockerTrendPanel';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import { buildBlockerTrend, summarizeBlockers } from '../blockerGroups';
import type { InsightsRecord, InsightsResponse } from '../types';
import { num } from '../utils';
import { RANGE_OPTIONS } from '../ranges';

interface InsightsScreenProps {
  data: InsightsResponse | null;
  loaded: boolean;
  busy: boolean;
  range: string;
  onRange: (range: string) => void;
}

type Gran = 'day' | 'week' | 'month';
type Dim = 'periods' | 'stores' | 'categories' | 'subcats' | 'brands' | 'blockers' | 'stages' | 'tiers' | 'needTypes' | 'priorities';
type Filters = Record<Dim, string[]>;
const EMPTY_FILTERS: Filters = {
  periods: [], stores: [], categories: [], subcats: [], brands: [], blockers: [], stages: [], tiers: [], needTypes: [], priorities: [],
};

// ---- time bucketing (client-side day/week/month) ----
function isoWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
function bucketOf(r: InsightsRecord, g: Gran): string {
  const day = r.day || '';
  if (g === 'month') return r.month || day.slice(0, 7);
  if (!day) return r.month || '';
  return g === 'day' ? day : isoWeek(day);
}
function periodLabel(code: string): string {
  if (/^\d{4}-W\d{2}$/.test(code)) return `T${code.slice(6)}/${code.slice(2, 4)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(code)) return `${code.slice(8)}/${code.slice(5, 7)}`;
  if (/^\d{4}-\d{2}$/.test(code)) return `Th${code.slice(5)}/${code.slice(2, 4)}`;
  return code;
}

const SIMPLE: Partial<Record<Dim, (r: InsightsRecord) => string>> = {
  stores: r => r.store_code || '',
  categories: r => r.ai_category || '',
  subcats: r => r.ai_subcategory_name || '',
  blockers: r => r.ai_blocker_type || '',
  stages: r => r.ai_deal_stage || '',
  tiers: r => r.ai_value_tier || '',
  needTypes: r => r.ai_need_type || '',
  priorities: r => r.ai_followup_priority || '',
};

const CAT_LABEL: Record<string, string> = {
  PC: 'PC / Máy bộ', LAPTOP: 'Laptop', APPLE: 'Apple', MAN_HINH: 'Màn hình', LINH_KIEN: 'Linh kiện',
  GEAR: 'Gaming Gear', GHE_BAN: 'Ghế / Bàn', THIET_BI_MANG: 'Mạng', PHU_KIEN: 'Phụ kiện',
  VAN_PHONG: 'Văn phòng', PHAN_MEM: 'Phần mềm', DICH_VU: 'Dịch vụ', OTHER: 'Khác', UNKNOWN: 'Chưa rõ',
};
const BLOCKER_LABEL: Record<string, string> = {
  out_of_stock: 'Hết hàng', demo_unavailable: 'Thiếu demo', price_budget: 'Giá / ngân sách',
  customer_left_early: 'Khách về sớm', comparing_shops: 'So sánh nơi khác', just_browsing: 'Chỉ dạo xem',
  technical_issue: 'Vướng kỹ thuật', not_in_business: 'Không kinh doanh', internal_approval: 'Chờ duyệt nội bộ',
  waiting_response: 'Chờ phản hồi', missing_contact: 'Thiếu liên hệ', unknown: 'Chưa xác định',
};
const STAGE_LABEL: Record<string, string> = {
  new_consulting: 'Mới tư vấn', followup_needed: 'Cần chăm tiếp', stock_blocked: 'Vướng hàng',
  demo_blocked: 'Vướng demo', deposit_pending: 'Đã cọc', closed_won: 'Đã chốt',
  service_recheck: 'Dịch vụ', lost_unknown: 'Mất dấu',
};
const PRIO_LABEL: Record<string, string> = { p0: 'P0 · gấp', p1: 'P1 · cao', p2: 'P2 · theo dõi', p3: 'P3 · thấp' };

function labelFor(dim: Dim, code: string): string {
  if (dim === 'categories') return CAT_LABEL[code] || code;
  if (dim === 'blockers') return BLOCKER_LABEL[code] || code;
  if (dim === 'stages') return STAGE_LABEL[code] || code;
  if (dim === 'priorities') return PRIO_LABEL[code] || code;
  if (dim === 'periods') return periodLabel(code);
  return code;
}
const DIM_TITLE: Record<Dim, string> = {
  periods: 'Kỳ', stores: 'Cửa hàng', categories: 'Ngành hàng', subcats: 'Loại sản phẩm', brands: 'Thương hiệu',
  blockers: 'Lý do chưa mua', stages: 'Giai đoạn', tiers: 'Giá trị', needTypes: 'Nhu cầu', priorities: 'Ưu tiên',
};

function countByKey(records: InsightsRecord[], keyFn: (r: InsightsRecord) => string): Array<[string, number]> {
  const m = new Map<string, number>();
  records.forEach(r => { const k = keyFn(r); if (k) m.set(k, (m.get(k) || 0) + 1); });
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}
function countByBrand(records: InsightsRecord[]): Array<[string, number]> {
  const m = new Map<string, number>();
  records.forEach(r => (r.ai_brands || []).forEach(b => { if (b) m.set(b, (m.get(b) || 0) + 1); }));
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

function barDS(entries: Array<[string, number]>, selected: string[], base: string): ChartDataset[] {
  const sel = new Set(selected);
  return [{
    label: 'Số lượt', data: entries.map(e => e[1]), type: 'bar',
    backgroundColor: entries.map(e => (sel.size === 0 || sel.has(e[0])) ? `${base}cc` : `${base}33`),
    borderColor: base, borderWidth: 1, borderRadius: 5, maxBarThickness: 56,
  } as unknown as ChartDataset];
}

// Right-size chart height to content (low-cardinality vertical → compact; horizontal grows per bar).
const vH = (n: number) => (n <= 5 ? 200 : 250);
const hH = (n: number) => Math.min(340, Math.max(140, n * 26 + 60));

interface ProdAgg { name: string; brand: string; category: string; count: number; stock: number; high: number; closed: number; }

function inRange(day: string | null | undefined, range: string): boolean {
  if (range === 'all') return true;
  if (!day) return false;
  const d = new Date(`${day}T00:00:00`).getTime();
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const today = now.getTime(); const D = 86400000;
  if (range === 'today') return d === today;
  if (range === 'yesterday') return d === today - D;
  const span: Record<string, number> = { '7d': 7, '28d': 28, '3m': 90, '6m': 180 };
  return d >= today - ((span[range] || 1) - 1) * D;
}

export function InsightsScreen({ data, loaded, busy, range, onRange }: InsightsScreenProps) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [gran, setGran] = useState<Gran>('day');
  const records = data?.records || [];
  const appliedRange = data?.meta?.range || range;
  const rangedRecords = useMemo(() => records.filter(r => inRange(r.day, appliedRange)), [records, appliedRange]);

  const toggle = (dim: Dim, value: string) => {
    if (!value) return;
    setFilters(prev => {
      const cur = prev[dim];
      return { ...prev, [dim]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
    });
  };
  const clearAll = () => setFilters(EMPTY_FILTERS);
  const setGranularity = (g: Gran) => { setGran(g); setFilters(prev => ({ ...prev, periods: [] })); };
  const changeRange = (v: string) => { onRange(v); setFilters(prev => ({ ...prev, periods: [] })); };
  const activeCount = (Object.keys(filters) as Dim[]).reduce((n, d) => n + filters[d].length, 0);

  const matches = (r: InsightsRecord, dim: Dim, vals: string[]): boolean => {
    if (vals.length === 0) return true;
    if (dim === 'periods') return vals.includes(bucketOf(r, gran));
    if (dim === 'brands') return (r.ai_brands || []).some(b => vals.includes(b));
    const f = SIMPLE[dim];
    return f ? vals.includes(f(r)) : true;
  };

  const ctx = useMemo(() => {
    const dims = Object.keys(filters) as Dim[];
    const apply = (except: Dim | null) => rangedRecords.filter(r => dims.every(d => d === except || matches(r, d, filters[d])));
    return {
      all: apply(null), periods: apply('periods'), stores: apply('stores'), categories: apply('categories'),
      subcats: apply('subcats'), brands: apply('brands'), blockers: apply('blockers'), stages: apply('stages'),
    };
  }, [rangedRecords, filters, gran]);

  const filtered = ctx.all;
  const total = filtered.length;
  const classified = filtered.filter(r => r.ai_category && r.ai_category !== 'UNKNOWN').length;
  const highValue = filtered.filter(r => r.high_value_flag).length;
  const stockGap = filtered.filter(r => r.stock_gap || r.demo_gap).length;
  const followup = filtered.filter(r => !r.duplicate_of_capture_id
    && ['p0', 'p1', 'p2'].includes(r.ai_followup_priority || '')
    && (r.ai_deal_stage !== 'closed_won' || r.high_value_flag)).length;
  const pct = (n: number) => total ? `${Math.round((n / total) * 100)}%` : '0%';

  // trend buckets at the selected granularity (honours other filters)
  const periodEntries = useMemo(() => {
    const m = new Map<string, { n: number; high: number; unknown: number }>();
    ctx.periods.forEach(r => {
      const k = bucketOf(r, gran); if (!k) return;
      const e = m.get(k) || { n: 0, high: 0, unknown: 0 };
      e.n++; if (r.high_value_flag) e.high++; if (r.ai_category === 'UNKNOWN') e.unknown++;
      m.set(k, e);
    });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [ctx.periods, gran]);

  const blockerTrendData = useMemo(() => {
    const input = ctx.periods.map(r => ({
      period: bucketOf(r, gran),
      blocker: r.ai_blocker_type,
      count: 1,
    }));
    return {
      points: buildBlockerTrend(input, period => period),
      summary: summarizeBlockers(input),
    };
  }, [ctx.periods, gran]);

  const catEntries = countByKey(ctx.categories, r => r.ai_category || '');
  const storeEntries = countByKey(ctx.stores, r => r.store_code || '');
  const blockerEntries = countByKey(ctx.blockers.filter(r => r.ai_blocker_type && r.ai_blocker_type !== 'no_blocker'), r => r.ai_blocker_type || '');
  const stageEntries = countByKey(ctx.stages, r => r.ai_deal_stage || '');
  const brandEntries = countByBrand(ctx.brands);
  const subcatEntries = countByKey(ctx.subcats, r => r.ai_subcategory_name || '').slice(0, 14);

  // PM/MKT: explode ai_products across the filtered records -> product lineup demand
  const products = useMemo(() => {
    const m = new Map<string, ProdAgg>();
    filtered.forEach(r => (r.ai_products || []).forEach(p => {
      const name = (p?.name || '').trim(); if (!name) return;
      const key = name.toLowerCase();
      let a = m.get(key);
      if (!a) { a = { name, brand: p.brand || '', category: p.category || r.ai_category || '', count: 0, stock: 0, high: 0, closed: 0 }; m.set(key, a); }
      a.count++;
      if (r.stock_gap || r.demo_gap) a.stock++;
      if (r.high_value_flag) a.high++;
      if (r.ai_deal_stage === 'closed_won') a.closed++;
    }));
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [filtered]);
  const lostDemand = products.filter(p => p.stock > 0).sort((a, b) => b.stock - a.stock).slice(0, 20);

  if (busy && !loaded) return <div className="card empty">Đang tải dữ liệu tư vấn tại cửa hàng...</div>;
  if (loaded && records.length === 0) return <div className="card empty">Chưa có dữ liệu tư vấn trong khoảng thời gian này.</div>;

  return (
    <>
      <section className="card space" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b>Khoảng thời gian</b>
        <div className="seg">
          {RANGE_OPTIONS.map(o => (
            <button key={o.v} type="button" className={`tab ${range === o.v ? 'active' : ''}`} disabled={busy} onClick={() => changeRange(o.v)}>{o.l}</button>
          ))}
        </div>
        <span className="muted">· {num(rangedRecords.length)} lượt trong khoảng · biểu đồ mặc định theo ngày</span>
      </section>
      <section className="grid kpis">
        <KpiCard label="Lượt khách (đã lọc)" value={num(total)} sub={`${num(records.length)} lượt trong khoảng`} tone="blue" />
        <KpiCard label="Đã phân loại" value={pct(classified)} sub={`${num(classified)} có ngành hàng`} tone="green" />
        <KpiCard label="Cần chăm sóc" value={num(followup)} sub="Hàng đợi P0/P1/P2" tone="amber" />
        <KpiCard label="Khách giá trị cao" value={num(highValue)} sub="Theo nhu cầu và ngành hàng" tone="violet" />
        <KpiCard label="Thiếu hàng / demo" value={num(stockGap)} sub={`${num(lostDemand.length)} sản phẩm bị chặn`} tone="red" />
        <KpiCard label="Sản phẩm được hỏi" value={num(products.length)} sub="Lineup khách quan tâm" tone="cyan" />
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Bộ lọc chéo</h2>
          <span>Bấm cột trong biểu đồ để lọc · {activeCount ? `${activeCount} bộ lọc đang bật` : 'chưa lọc'}</span>
        </div>
        <div className="filter-pills">
          {activeCount === 0 && <span className="muted">Bấm cột bất kỳ (ngành hàng, thương hiệu, kỳ, lý do…) để lọc toàn bộ dashboard.</span>}
          {(Object.keys(filters) as Dim[]).flatMap(dim => filters[dim].map(val => (
            <button key={`${dim}:${val}`} type="button" className="pill filter" onClick={() => toggle(dim, val)}>
              <span className="muted">{DIM_TITLE[dim]}:</span> {labelFor(dim, val)} <X size={12} />
            </button>
          )))}
          {activeCount > 0 && <button type="button" className="pill ghost" onClick={clearAll}>Xóa hết</button>}
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Xu hướng lượt khách</h2>
          <div className="seg">
            {(['day', 'week', 'month'] as Gran[]).map(g => (
              <button key={g} type="button" className={`tab ${gran === g ? 'active' : ''}`} onClick={() => setGranularity(g)}>
                {g === 'day' ? 'Ngày' : g === 'week' ? 'Tuần' : 'Tháng'}
              </button>
            ))}
          </div>
        </div>
        <div className="chart tall">
          <ChartBox
            labels={periodEntries.map(e => periodLabel(e[0]))}
            datasets={[
              ...barDS(periodEntries.map(e => [e[0], e[1].n]), filters.periods, '#2563eb'),
              { label: 'Lead giá trị cao', data: periodEntries.map(e => e[1].high), type: 'line', borderColor: '#7c3aed', backgroundColor: '#7c3aed22', borderWidth: 2, tension: 0.35, pointRadius: 3, yAxisID: 'y' } as unknown as ChartDataset,
              { label: 'Chưa rõ ngành hàng', data: periodEntries.map(e => e[1].unknown), type: 'line', borderColor: '#dc2626', backgroundColor: '#dc262622', borderWidth: 2, borderDash: [5, 3], tension: 0.35, pointRadius: 3, yAxisID: 'y' } as unknown as ChartDataset,
            ]}
            onPointClick={({ index }) => toggle('periods', periodEntries[index][0])}
          />
        </div>
      </section>

      <BlockerTrendPanel
        points={blockerTrendData.points}
        summary={blockerTrendData.summary}
        periodLabel={periodLabel}
      />

      <section className="grid split space atop">
        <div className="card">
          <div className="section-title"><h2>Ngành hàng</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: vH(catEntries.length) }}><ChartBox labels={catEntries.map(e => labelFor('categories', e[0]))} datasets={barDS(catEntries, filters.categories, '#2563eb')} options={{ plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('categories', catEntries[index][0])} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Cửa hàng</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: vH(storeEntries.length) }}><ChartBox labels={storeEntries.map(e => e[0])} datasets={barDS(storeEntries, filters.stores, '#0891b2')} options={{ plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('stores', storeEntries[index][0])} /></div>
        </div>
      </section>

      <section className="grid split space atop">
        <div className="card">
          <div className="section-title"><h2>Lý do khách chưa mua</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: hH(blockerEntries.length) }}><ChartBox labels={blockerEntries.map(e => labelFor('blockers', e[0]))} datasets={barDS(blockerEntries, filters.blockers, '#b45309')} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('blockers', blockerEntries[index][0])} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Giai đoạn phễu</h2><span>bấm để lọc</span></div>
          <div className="chart" style={{ height: vH(stageEntries.length) }}><ChartBox labels={stageEntries.map(e => labelFor('stages', e[0]))} datasets={barDS(stageEntries, filters.stages, '#6d28d9')} options={{ plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('stages', stageEntries[index][0])} /></div>
        </div>
      </section>

      {/* ===== PRODUCT / LINEUP — cho PM & MKT ===== */}
      <section className="card space" style={{ borderTop: '3px solid var(--cyan)' }}>
        <div className="section-title">
          <h2>Sản phẩm &amp; thương hiệu khách quan tâm</h2>
          <span>Bóc từ sản phẩm khách hỏi (ai_products) · theo bộ lọc hiện tại</span>
        </div>
        <div className="grid split atop">
          <div>
            <div className="section-title"><h2>Thương hiệu được hỏi</h2><span>bấm để lọc</span></div>
            <div className="chart" style={{ height: hH(Math.min(brandEntries.length, 14)) }}><ChartBox labels={brandEntries.slice(0, 14).map(e => e[0])} datasets={barDS(brandEntries.slice(0, 14), filters.brands, '#0891b2')} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('brands', brandEntries[index][0])} /></div>
          </div>
          <div>
            <div className="section-title"><h2>Loại sản phẩm</h2><span>bấm để lọc</span></div>
            <div className="chart" style={{ height: hH(subcatEntries.length) }}><ChartBox labels={subcatEntries.map(e => e[0])} datasets={barDS(subcatEntries, filters.subcats, '#16a34a')} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => toggle('subcats', subcatEntries[index][0])} /></div>
          </div>
        </div>
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Top sản phẩm khách hỏi</h2><span>{num(products.length)} sản phẩm · demand lineup</span></div>
          <div className="scroll" style={{ maxHeight: 380 }}>
            <table>
              <thead><tr><th>Sản phẩm</th><th>Thương hiệu</th><th className="right">Lượt hỏi</th><th className="right">Đã chốt</th><th className="right">Thiếu hàng</th><th className="right">Giá trị cao</th></tr></thead>
              <tbody>{products.slice(0, 40).map((p, i) => (
                <tr key={`${p.name}-${i}`}>
                  <td>{p.name}</td>
                  <td className="muted">{p.brand || '—'}</td>
                  <td className="right tnum"><b>{num(p.count)}</b></td>
                  <td className="right tnum">{p.closed || '—'}</td>
                  <td className="right tnum">{p.stock ? <Tag tone="red">{p.stock}</Tag> : '—'}</td>
                  <td className="right tnum">{p.high ? <Tag tone="violet">{p.high}</Tag> : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Cầu chưa đáp ứng (thiếu hàng / demo)</h2><span>cơ hội nhập hàng · trưng bày</span></div>
          <div className="scroll" style={{ maxHeight: 380 }}>
            <table>
              <thead><tr><th>Sản phẩm khách cần</th><th>Thương hiệu</th><th className="right">Số lượt bị chặn</th></tr></thead>
              <tbody>{lostDemand.length === 0 ? (
                <tr><td colSpan={3} className="muted">Không có sản phẩm nào bị chặn theo bộ lọc hiện tại.</td></tr>
              ) : lostDemand.map((p, i) => (
                <tr key={`lost-${p.name}-${i}`}>
                  <td>{p.name}</td>
                  <td className="muted">{p.brand || '—'}</td>
                  <td className="right tnum"><Tag tone="red">{p.stock}</Tag></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Chi tiết lượt khách</h2>
          <span>{num(filtered.length)} dòng (theo bộ lọc) · SĐT đã ẩn</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr><th>Ngày</th><th>Store</th><th>Ngành hàng</th><th>Loại sản phẩm</th><th>Giai đoạn</th><th>Lý do chưa mua</th><th>Ưu tiên</th><th>Nhu cầu (đã ẩn SĐT)</th></tr></thead>
            <tbody>
              {filtered.slice(0, 200).map(r => (
                <tr key={r.capture_id}>
                  <td className="tnum">{r.day}</td>
                  <td><b>{r.store_code}</b></td>
                  <td><Tag tone={r.ai_category === 'UNKNOWN' ? 'gray' : 'blue'}>{labelFor('categories', r.ai_category || '')}</Tag></td>
                  <td className="muted">{r.ai_subcategory_name || '—'}</td>
                  <td>{r.ai_deal_stage ? labelFor('stages', r.ai_deal_stage) : '—'}</td>
                  <td className="muted">{r.ai_blocker_type && r.ai_blocker_type !== 'no_blocker' ? labelFor('blockers', r.ai_blocker_type) : '—'}</td>
                  <td>{r.ai_followup_priority ? <Tag tone={r.ai_followup_priority === 'p0' ? 'red' : r.ai_followup_priority === 'p1' ? 'amber' : 'gray'}>{labelFor('priorities', r.ai_followup_priority)}</Tag> : '—'}</td>
                  <td className="muted">{r.ai_need_summary || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
